import React, { useMemo, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
} from 'antd';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { updateWorkReportEntry } from '../../abWorkReporting/state/abWorkReporting.actions';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import {
  getABWorkStatusSelectOptions,
  getBidItemOptionsForABWorkReportEdit,
  getFiscalPeriod,
  getSegmentOptionsForABWorkReportEdit,
} from '../../boards/BoardCardABWorkReporting/BoardCardABWorkReportingHelpers';
import ManualEntryDatePicker from '../../clock/ManualEntry/ManualEntryDatePicker';
import { getIdMap } from '../../helpers/helpers';
import { DateTime } from 'luxon';

const WORK_REPORT_COMMENTS_MAX = 500;

export default function ABWorkReportEditModal({
  editEntry,
  setEditEntry,
  bidItemStatuses = {},
  dateField,
  segmentBucketTemplateId,
  bidItemBucketTemplateId,
  bucketMap,
  statuses = {},
}) {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [prevEditEntry, setPrevEditEntry] = useState(null);
  const [selectedSegmentId, setSelectedSegmentId] = useState();

  const currentBidItemStatusId = editEntry?.['bidItem.statusId'];
  const currentBidItemStatus = Object.values(bidItemStatuses).find((s) => s.id === currentBidItemStatusId);
  const bidItemStatusSelectLocked = currentBidItemStatus?.status === 'Historical';
  const statusOptions = getABWorkStatusSelectOptions({
    relevantStatuses: bidItemStatuses,
    currentStatusId: currentBidItemStatusId,
  }).map(({ id, status }) => ({
    value: id,
    label: status,
  }));

  const segmentOptions = useMemo(() => (
    getSegmentOptionsForABWorkReportEdit({
      itemId: editEntry?.itemId,
      segmentBucketTemplateId,
      bidItemBucketTemplateId,
      bucketMap,
      bidItemName: editEntry?.['bidItem.name'],
      statuses,
    })
  ), [editEntry?.itemId, segmentBucketTemplateId, bidItemBucketTemplateId, bucketMap, statuses, editEntry?.['bidItem.name']]);

  const segmentOptionMap = getIdMap(segmentOptions, 'value');

  const bidItemOptions = useMemo(() => (
    getBidItemOptionsForABWorkReportEdit({
      segmentId: selectedSegmentId,
      bidItemBucketTemplateId,
      bucketMap,
      statuses,
    })
  ), [selectedSegmentId, bidItemBucketTemplateId, bucketMap, statuses]);

  const bidItemOptionMap = getIdMap(bidItemOptions, 'value');

  if (editEntry && editEntry !== prevEditEntry) {
    setPrevEditEntry(editEntry);
    form.setFieldsValue({
      quantity: editEntry.quantity ?? 0,
      comments: editEntry.comments ?? '',
      statusId: editEntry['bidItem.statusId'] ?? undefined,
      dateWorked: editEntry.dateWorked ?? undefined,
      segmentId: editEntry.segmentId ?? undefined,
      bidItemId: editEntry.bidItemId ?? undefined,
    });
    setSelectedSegmentId(editEntry.segmentId);
  }

  const dateWorkedRules = [
    { required: true, message: 'No date worked' },
    () => ({
      validator(_, value) {
        if (!editEntry) return Promise.resolve();
        if (!dateField) {
          return Promise.reject(new Error('No date field'));
        }
        if (!editEntry[dateField]) {
          return Promise.reject(new Error('Date field is empty on bid item.'));
        }
        if (!value) {
          return Promise.resolve();
        }
        const bidItemFiscalPeriod = getFiscalPeriod(editEntry[dateField]) || 'None';
        const entryFiscalPeriod = getFiscalPeriod(value, 'yyyyMMdd') || 'None';
        if (bidItemFiscalPeriod !== entryFiscalPeriod) {
          const msg = [
            'Fiscal period does not match bid item and date worked.',
            `Bid item: ${bidItemFiscalPeriod}, Date Worked: ${entryFiscalPeriod}`,
          ].join(' ');
          return Promise.reject(new Error(msg));
        }
        return Promise.resolve();
      },
    }),
  ];

  const onCancel = () => {
    form.resetFields();
    setPrevEditEntry(null);
    setEditEntry(null);
    setSelectedSegmentId(null);
  };

  const onSave = async () => {
    if (!editEntry) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        quantity: values.quantity,
        comments: values.comments || null,
        statusId: values.statusId || null,
        dateWorked: values.dateWorked ?? null,
      };

      // The reason why we update the bid item id instead of segment
      // Is because each segment under an item shares the same bid items
      // So we change the entry to be under the new bid item
      // ie. Item A has Segment A and Segment B. Each segment will have the same bid items
      // So when we update the entry to be from Segment A to Segment B, we need to change it to the
      // new bid item for Segment B
      if (values.segmentId !== editEntry.segmentId && segmentOptionMap[values.segmentId]) {
        payload.segmentId = values.segmentId;
        payload.bidItemId = segmentOptionMap[values.segmentId].bidItemId;
      }

      // Direct bid item swap
      // Update segment id to the segment that owns the chosen bid item.
      if (values.bidItemId && values.bidItemId !== editEntry.bidItemId) {
        const chosen = bidItemOptionMap[values.bidItemId];
        if (chosen) {
          payload.bidItemId = values.bidItemId;
          payload.segmentId = chosen.segmentId;
        }
      }

      // From client side to server format
      payload.dateWorked = DateTime.fromFormat(values.dateWorked, 'yyyyMMdd').toSQLDate();

      const success = await dispatch(updateWorkReportEntry(
        editEntry.id,
        payload,
        editEntry.bidItemId,
      ));
      setSaving(false);
      if (success) {
        form.resetFields();
        setPrevEditEntry(null);
        setEditEntry(null);
      } else {
        message.error('Failed to update work report entry');
      }
    } catch (err) {
      setSaving(false);
      if (err?.errorFields) return;
      message.error('Failed to update work report entry');
    }
  };

  const onSegmentChange = (newSegmentId) => {
    // Default to the bid item with the same name as the entry's
    // current bid item; fall back to the first available so the
    // field is never left referencing a stale segment's bid item.
    const matchingName = bucketMap?.[editEntry?.bidItemId]?.name;
    const options = getBidItemOptionsForABWorkReportEdit({
      segmentId: newSegmentId,
      bidItemBucketTemplateId,
      bucketMap,
      statuses,
    });
    const nextBidItem = options.find(
      (opt) => opt.label === matchingName,
    ) || options[0];
    form.setFieldsValue({ bidItemId: nextBidItem?.value, segmentId: newSegmentId });
    setSelectedSegmentId(newSegmentId);
  };

  if (!editEntry) return null;

  return (
    <Modal
      title="Edit AB Work Report"
      visible={!!editEntry}
      onCancel={onCancel}
      footer={[
        <OnTraccrButton
          key="cancel"
          title="Cancel"
          type="cancel"
          onClick={onCancel}
        />,
        <OnTraccrButton
          key="save"
          title="Save"
          onClick={onSave}
          loading={saving}
        />,
      ]}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Accomplishment"
          name="quantity"
          rules={[{ required: true, message: 'Enter accomplishment' }]}
        >
          <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          label="Date worked"
          name="dateWorked"
          rules={dateWorkedRules}
        >
          <ManualEntryDatePicker allowClear style={{ width: '100%' }} format="yyyyMMDD" />
        </Form.Item>
        {segmentOptions.length > 0 ? (
          <Form.Item
            label="Segment"
            name="segmentId"
            rules={[{ required: true, message: 'Select a segment' }]}
          >
            <Select
              placeholder="Select segment"
              optionFilterProp="label"
              options={segmentOptions}
              disabled={segmentOptions.length < 2}
              onChange={onSegmentChange}
              showSearch
            />
          </Form.Item>
        ) : null}
        {bidItemOptions.length > 0 ? (
          <Form.Item
            label="Bid Item"
            name="bidItemId"
            rules={[{ required: true, message: 'Select a bid item' }]}
          >
            <Select
              placeholder="Select bid item"
              optionFilterProp="label"
              options={bidItemOptions}
              disabled={bidItemOptions.length < 2}
              showSearch
            />
          </Form.Item>
        ) : null}
        <Form.Item
          label="Comments"
          name="comments"
          rules={[{ max: WORK_REPORT_COMMENTS_MAX, message: `Comments must be at most ${WORK_REPORT_COMMENTS_MAX} characters` }]}
        >
          <Input.TextArea rows={3} maxLength={WORK_REPORT_COMMENTS_MAX} showCount />
        </Form.Item>
        <Form.Item label="Bid Item Status" name="statusId">
          <Select
            allowClear={!bidItemStatusSelectLocked}
            disabled={bidItemStatusSelectLocked}
            placeholder="Select status"
            optionFilterProp="label"
            options={statusOptions}
            onChange={() => {
              form.validateFields(['quantity']).catch(() => {});
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

ABWorkReportEditModal.propTypes = {
  editEntry: PropTypes.shape({
    id: PropTypes.string,
    bidItemId: PropTypes.string,
    itemId: PropTypes.string,
    segmentId: PropTypes.string,
    quantity: PropTypes.number,
    comments: PropTypes.string,
    dateWorked: PropTypes.string,
    bucketTemplateId: PropTypes.string,
    'bidItem.statusId': PropTypes.string,
    'bidItem.name': PropTypes.string,
  }),
  setEditEntry: PropTypes.func.isRequired,
  bidItemStatuses: PropTypes.objectOf(PropTypes.shape({
    status: PropTypes.string,
  })),
  dateField: PropTypes.string,
  segmentBucketTemplateId: PropTypes.string,
  bidItemBucketTemplateId: PropTypes.string,
  bucketMap: PropTypes.shape({}),
  statuses: PropTypes.shape({}),
};

ABWorkReportEditModal.defaultProps = {
  editEntry: null,
  bidItemStatuses: {},
  dateField: undefined,
  segmentBucketTemplateId: undefined,
  bidItemBucketTemplateId: undefined,
  bucketMap: {},
  statuses: {},
};
